import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import { Pump } from "../../dtos/pump";
import { Field } from "../../dtos/field";
import { Sensor } from "../../dtos/sensor";
import { AjaxResult } from "../../enums/ajaxResult";
import { PumpCard } from "./pumpCard";
import { FieldCard } from "./fieldCard";
import { SensorCard } from "./sensorCard";
import * as mui from "@material-ui/core";
import GoogleMapReact from "google-map-react";
import { useWindowSize } from "@react-hook/window-size";
import { SearchRounded } from "@material-ui/icons";
import { MapMarker } from "./mapMarker";
import { ClusterMarker } from "./clusterMarker";
import { useMapState } from "./useMapState";
import { useClusteredPumps } from "./useClusteredPumps";
import { useApi } from "../apiService";
import { AuthContext } from "../../auth/authContext";
import { makeStyles } from "@material-ui/styles";
import { useMediaQuery } from "@material-ui/core";
import { UserRole } from "../../enums/userRoles";
import { HomeCard } from "../../enums/homeCard";
import { MarkerCategory } from "../../enums/markerCategory";
import Globals from "../../globals";
import { ApiResult } from "../../rest/baseApi";
import { useConfirmDialog } from "../confirmDialogService";
import { useMessage } from "../messageService";
import { MapViewType, MapTypeSelect } from "./mapTypeSelect";
import { CardTypeSelect } from "./cardSelect";
import linq from "linq";
import { PumpWeatherCard } from "./pumpWeatherCard";
import { PivotCard } from "./pivotCard";
import { usePivots } from "../../hooks/usePivots";
import { AddPivotModal } from "../pivotPage/addPivotModal";
import { useTheme } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import { Link } from "react-router-dom";

const FEET_TO_METERS = 0.3048;

const normalizeDegrees = (value?: number) => {
  if (value === undefined || value === null) {
    return 0;
  }

  return ((value % 360) + 360) % 360;
};

const toLatLngFromBearing = (
  centerLat: number,
  centerLon: number,
  distanceMeters: number,
  bearingDegrees: number
) => {
  const earthRadiusMeters = 6378137;
  const angularDistance = distanceMeters / earthRadiusMeters;
  const bearing = (bearingDegrees * Math.PI) / 180;
  const lat1 = (centerLat * Math.PI) / 180;
  const lon1 = (centerLon * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
    Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );

  const lon2 = lon1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );

  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (lon2 * 180) / Math.PI
  };
};

const buildPivotSectorPath = (
  centerLat: number,
  centerLon: number,
  radiusMeters: number,
  startDegrees: number,
  stopDegrees: number,
  segmentCount = 48
) => {
  const normalizedStart = normalizeDegrees(startDegrees);
  let normalizedStop = normalizeDegrees(stopDegrees);

  if (normalizedStop <= normalizedStart) {
    normalizedStop += 360;
  }

  const totalSweep = normalizedStop - normalizedStart;
  const points = [{ lat: centerLat, lng: centerLon }];

  for (let i = 0; i <= segmentCount; i += 1) {
    const bearing = normalizedStart + (totalSweep * i) / segmentCount;
    points.push(toLatLngFromBearing(centerLat, centerLon, radiusMeters, bearing));
  }

  points.push({ lat: centerLat, lng: centerLon });
  return points;
};

const buildPerpendicularTickPath = (
  centerLat: number,
  centerLon: number,
  armBearingDegrees: number,
  distanceMeters: number,
  tickLengthMeters: number
) => {
  const midpoint = toLatLngFromBearing(centerLat, centerLon, distanceMeters, armBearingDegrees);
  const start = toLatLngFromBearing(midpoint.lat, midpoint.lng, tickLengthMeters / 2, armBearingDegrees - 90);
  const end = toLatLngFromBearing(midpoint.lat, midpoint.lng, tickLengthMeters / 2, armBearingDegrees + 90);

  return [start, end];
};

export const HomePage = () => {
  const { buttonContainer, buttonContainerRight, searchBar, stopButton, mapTypeStyle } = useStyles();
  const { customerId, role } = useContext(AuthContext);
  const { pumpsApi, fieldsApi, sensorsApi } = useApi();
  const [pumps, setPumps] = useState<Pump[]>([]);
  const [fields, setFields] = useState<Field[]>([]);
  const [pumpFieldsLoaded, setPumpFieldsLoaded] = useState<number[]>([]);
  const [pumpSensorsLoaded, setPumpSensorsLoaded] = useState<number[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const mapRef = useRef<any>();
  const mapsApiRef = useRef<any>();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const pivotOverlaysRef = useRef<any[]>([]);
  const previousCardTypeRef = useRef<HomeCard | null>(null);
  const [mapRefIsLoaded, setMapRefIsLoaded] = useState(false);
  const { bounds, mapCenter, mapZoom, setBounds, setMapCenter, setMapZoom } = useMapState();
  const height = useWindowSize()[1];
  const pageContentHeight = `${height - 226}px`;
  const listContentHeight = `${height - 177}px`;
  const { showConfirmDialog } = useConfirmDialog();
  const { showMessage } = useMessage();
  const [mapViewType, setMapViewType] = useState<MapViewType>("satellite");
  const [cardType, setCardType] = useState<HomeCard>(HomeCard.Pumps);
  const [addPivotModalOpen, setAddPivotModalOpen] = useState(false);
  const [selectedPivotId, setSelectedPivotId] = useState<number | null>(null);
  const [showPivotGraphics, setShowPivotGraphics] = useState(true);
  const [mapContextMenu, setMapContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    lat: number;
    lng: number;
  } | null>(null);

  const { pivots, addPivot } = usePivots();
  const pivotsWithCoords = useMemo(() => pivots.filter(p => !!p.lat && !!p.lon), [pivots]);

  useEffect(() => {
    const apiMethod =
      role === UserRole.Admin
        ? () => pumpsApi.getAll()
        : (role === UserRole.Manager || role === UserRole.Operator) &&
          customerId
          ? () => pumpsApi.getForCustomer(customerId)
          : () => pumpsApi.getMyPumps();
    apiMethod().then(r => {
      if (r.result === AjaxResult.Success && r.data) {

        var orderedPumps = r.data.sort(function (a: Pump, b: Pump) {
          return a.name.trim().localeCompare(b.name.trim(), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        });

        setPumps(orderedPumps);
      }
    });
  }, [pumpsApi, customerId, role]);
  const pumpsWithCoords = useMemo(() => pumps.filter(p => !!p.lat && !!p.lon), [
    pumps
  ]);

  const sensorsWithCoords = useMemo(
    () =>
      sensors.filter(
        s =>
          !!s.lat &&
          !!s.lon
      ),
    [sensors]
  );
  const fieldsWithCoords = useMemo(
    () =>
      fields.filter(
        f =>
          !!f.lat &&
          !!f.lon
      ),
    [fields]
  );
  const fitMapToMarkers = (markers: Array<{ lat?: number; lon?: number }>) => {
    if (!mapRefIsLoaded || !markers.length) {
      return;
    }

    const markersLinq = linq.from(markers);
    const minLat = markersLinq.min(p => p.lat!);
    const minLong = markersLinq.min(p => p.lon!);
    const maxLat = markersLinq.max(p => p.lat!);
    const maxLong = markersLinq.max(p => p.lon!);

    if (
      minLat === undefined ||
      minLong === undefined ||
      maxLat === undefined ||
      maxLong === undefined
    ) {
      return;
    }

    if (maxLat - minLat > 0.0005 || maxLong - minLong > 0.0005) {
      (mapRef.current as any).fitBounds({
        east: maxLong,
        west: minLong,
        north: maxLat,
        south: minLat
      });
      return;
    }

    setMapCenter({
      lat: ((maxLat + minLat) / 2) as number,
      lng: ((maxLong + minLong) / 2) as number
    });
    setMapZoom(17);
  };
  const defaultCenter = useMemo(() => {
    if (!pumpsWithCoords || !pumpsWithCoords.length) {
      return undefined;
    }
    const pumpsLinq = linq.from(pumpsWithCoords);
    const minLat = pumpsLinq.min(p => p.lat);
    const minLong = pumpsLinq.min(p => p.lon);
    const maxLat = pumpsLinq.max(p => p.lat);
    const maxLong = pumpsLinq.max(p => p.lon);
    return {
      lat: ((maxLat + minLat) / 2) as number,
      lng: ((maxLong + minLong) / 2) as number
    };
  }, [pumpsWithCoords]);

  useEffect(() => {
    if (!pumpsWithCoords || !pumpsWithCoords.length || !mapRefIsLoaded) {
      return;
    }
    fitMapToMarkers(pumpsWithCoords.map(p => ({ lat: p.lat, lon: p.lon })));
  }, [mapRefIsLoaded, pumpsWithCoords]);

  const [searchText, setSearchText] = useState("");
  const filteredPumps = useMemo(() => {
    return searchText
      ? pumpsWithCoords.filter(
        p =>
          p.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
          p.serialNumber?.toString()?.indexOf(searchText) !== -1
      )
      : pumpsWithCoords;
  }, [pumpsWithCoords, searchText]);
  const filteredFields = useMemo(() => {
    return searchText
      ? fieldsWithCoords.filter(
        f =>
          f.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
          f.valveSerialNumber?.toString()?.indexOf(searchText) !== -1
      )
      : fieldsWithCoords;
  }, [fieldsWithCoords, searchText]);
  const filteredSensors = useMemo(() => {
    return searchText
      ? sensorsWithCoords.filter(
        s =>
          s.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
          s.serial?.toString()?.indexOf(searchText) !== -1
      )
      : sensorsWithCoords;
  }, [sensorsWithCoords, searchText]);
  const filteredPivots = useMemo(() => {
    return searchText
      ? pivotsWithCoords.filter(
        p =>
          p.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
          p.serial?.toString()?.indexOf(searchText) !== -1
      )
      : pivotsWithCoords;
  }, [pivotsWithCoords, searchText]);

  useEffect(() => {
    setSelectedPivotId(null);

    if (!mapRefIsLoaded) {
      return;
    }

    if (previousCardTypeRef.current === cardType) {
      return;
    }

    previousCardTypeRef.current = cardType;

    if (cardType === HomeCard.Pivots) {
      fitMapToMarkers(pivotsWithCoords.map(p => ({ lat: p.lat, lon: p.lon })));
      return;
    }

    if (cardType === HomeCard.Fields) {
      fitMapToMarkers(fieldsWithCoords.map(f => ({ lat: f.lat, lon: f.lon })));
      return;
    }

    if (cardType === HomeCard.Sensors) {
      fitMapToMarkers(sensorsWithCoords.map(s => ({ lat: s.lat, lon: s.lon })));
      return;
    }

    if (cardType === HomeCard.Pumps || cardType === HomeCard.PumpWeather) {
      fitMapToMarkers(pumpsWithCoords.map(p => ({ lat: p.lat, lon: p.lon })));
    }
  }, [cardType, fieldsWithCoords, mapRefIsLoaded, pivotsWithCoords, pumpsWithCoords, sensorsWithCoords]);

  useEffect(() => {
    pivotOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    pivotOverlaysRef.current = [];

    if (!mapRefIsLoaded || cardType !== HomeCard.Pivots || !showPivotGraphics || !mapRef.current || !mapsApiRef.current) {
      return;
    }

    const overlays = pivotsWithCoords
      .flatMap(pivot => {
        if (pivot.lat == null || pivot.lon == null || !pivot.length) {
          return [];
        }

        const radiusMeters = pivot.length * FEET_TO_METERS;
        const isSelected = selectedPivotId === pivot.id;
        const isRunning = pivot.online && pivot.status === "Running";
        const strokeColor = isRunning
          ? (isSelected ? "#1b5e20" : "#2e7d32")
          : (isSelected ? "#0d47a1" : "#1976d2");
        const fillColor = isRunning
          ? (isSelected ? "#66bb6a" : "#81c784")
          : (isSelected ? "#42a5f5" : "#90caf9");
        const angleBearing = pivot.anglePercent ?? 0;
        const currentAnglePoint = toLatLngFromBearing(
          pivot.lat,
          pivot.lon,
          radiusMeters,
          angleBearing
        );
        const angleLine = new mapsApiRef.current.Polyline({
          map: mapRef.current,
          path: [
            { lat: pivot.lat, lng: pivot.lon },
            currentAnglePoint
          ],
          strokeColor: "#ffffff",
          strokeOpacity: 0.98,
          strokeWeight: isSelected ? 3.5 : 2.5,
          zIndex: 20
        });
        const towerTicks = [0.28, 0.52, 0.76].map(ratio => new mapsApiRef.current.Polyline({
          map: mapRef.current,
          path: buildPerpendicularTickPath(
            pivot.lat!,
            pivot.lon!,
            angleBearing,
            radiusMeters * ratio,
            radiusMeters * 0.08
          ),
          strokeColor: "#ffffff",
          strokeOpacity: 0.98,
          strokeWeight: isSelected ? 2.5 : 2,
          zIndex: 21
        }));
        const endTick = new mapsApiRef.current.Polyline({
          map: mapRef.current,
          path: buildPerpendicularTickPath(
            pivot.lat,
            pivot.lon,
            angleBearing,
            radiusMeters * 0.97,
            radiusMeters * 0.1
          ),
          strokeColor: "#ffffff",
          strokeOpacity: 0.98,
          strokeWeight: isSelected ? 2.5 : 2,
          zIndex: 21
        });

        if ((pivot.pivot_type ?? "full") === "full") {
          const circle = new mapsApiRef.current.Circle({
            map: mapRef.current,
            center: { lat: pivot.lat, lng: pivot.lon },
            radius: radiusMeters,
            strokeColor,
            strokeOpacity: 0.9,
            strokeWeight: isSelected ? 2 : 1,
            fillColor,
            fillOpacity: isSelected ? 0.18 : 0.1
          });

          return [circle, angleLine, ...towerTicks, endTick];
        }

        const polygon = new mapsApiRef.current.Polygon({
          map: mapRef.current,
          paths: buildPivotSectorPath(
            pivot.lat,
            pivot.lon,
            radiusMeters,
            pivot.limit_angle_a ?? 0,
            pivot.limit_angle_b ?? 359
          ),
          strokeColor,
          strokeOpacity: 0.9,
          strokeWeight: isSelected ? 2 : 1,
          fillColor,
          fillOpacity: isSelected ? 0.2 : 0.12
        });

        return [polygon, angleLine, ...towerTicks, endTick];
      })
      .filter(Boolean);

    pivotOverlaysRef.current = overlays;

    return () => {
      overlays.forEach(overlay => overlay.setMap(null));
    };
  }, [cardType, mapRefIsLoaded, pivotsWithCoords, selectedPivotId, showPivotGraphics]);

  const loadSensorData = (bounds: any) => {
    const pumpsWithinBounds = pumpsWithCoords.filter(
      p =>
        p.lat <= bounds.ne.lat &&
        p.lat >= bounds.sw.lat &&
        p.lon <= bounds.ne.lng &&
        p.lon >= bounds.sw.lng
    );

    const pumpsToLoad = pumpsWithinBounds.filter(
      p => !pumpSensorsLoaded.find(l => l === p.id)
    );

    const sensorApiCalls = [] as Promise<ApiResult<any>>[];
    pumpsToLoad.forEach(p => sensorApiCalls.push(sensorsApi.getAllByPump(p.id)));
    Promise.all(sensorApiCalls).then(s => {
      setPumpSensorsLoaded([
        ...pumpSensorsLoaded,
        ...pumpsToLoad.map(p => p.id)
      ]);
      const newSensors = s
        .filter(r => r.result === AjaxResult.Success && r.data)
        .map(r => r.data as Sensor[])
        .flat();

      var orderedSensors = newSensors.sort(function (a, b) {
        return a.name.trim().localeCompare(b.name.trim(), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });

      setSensors([...sensors, ...orderedSensors]);
    });
  };
  //LoadFieldData
  const loadFieldData = (bounds: any) => {
    const pumpsWithinBounds = pumpsWithCoords.filter(
      p =>
        p.lat <= bounds.ne.lat &&
        p.lat >= bounds.sw.lat &&
        p.lon <= bounds.ne.lng &&
        p.lon >= bounds.sw.lng
    );

    const pumpsToLoad = pumpsWithinBounds.filter(
      p => !pumpFieldsLoaded.find(l => l === p.id)
    );

    const feildApiCalls = [] as Promise<ApiResult<any>>[];
    pumpsToLoad.forEach(p => feildApiCalls.push(fieldsApi.getByPumpId(p.id)));
    Promise.all(feildApiCalls).then(f => {
      setPumpFieldsLoaded([
        ...pumpFieldsLoaded,
        ...pumpsToLoad.map(p => p.id)
      ]);
      const newFields = f
        .filter(r => r.result === AjaxResult.Success && r.data)
        .map(r => r.data as Field[])
        .flat();

      var orderedFields = newFields.sort(function (a, b) {
        return a.name.trim().localeCompare(b.name.trim(), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });

      setFields([...fields, ...orderedFields]);
    });
  };

  const clusters = useClusteredPumps({
    pumpsWithCoords: cardType === HomeCard.Pivots ? [] : pumpsWithCoords,
    sensorsWithCoords: cardType === HomeCard.Pivots ? [] : sensorsWithCoords,
    fieldsWithCoords: cardType === HomeCard.Pivots ? [] : fieldsWithCoords,
    pivotsWithCoords,
    selectedPivotId,
    mapZoom,
    bounds,
    cardType
  });

  const tryStopAllPumps = () => {
    showConfirmDialog(`Are you sure you want to stop all pumps?`).then(
      confirmed => {
        if (confirmed) {
          pumpsApi.stopAllPumps().then(result => {
            if (result.result === AjaxResult.Success) {
              showMessage("Command sent to Pumps");
            } else if (result.messages) {
              result.messages.forEach(m => {
                showMessage(m);
              });
            }
          });
        }
      }
    );
  };

  const copyLatLon = async (lat: number, lng: number) => {
    const coordinates = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

    try {
      await navigator.clipboard.writeText(coordinates);
      showMessage(`Copied ${coordinates}`);
    } catch {
      showMessage(`Lat/Lon: ${coordinates}`);
    }
  };

  const closeMapContextMenu = () => {
    setMapContextMenu(null);
  };

  const suppressNativeContextMenu = (event: React.MouseEvent<HTMLElement> | MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const mapContainer = mapContainerRef.current;

    if (!mapContainer) {
      return;
    }

    const nativeContextMenuHandler = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    mapContainer.addEventListener("contextmenu", nativeContextMenuHandler, true);

    return () => {
      mapContainer.removeEventListener("contextmenu", nativeContextMenuHandler, true);
    };
  }, []);

  const theme = useTheme();

  return (
    <div>
      <AddPivotModal isVisible={addPivotModalOpen} onSave={addPivot} close={() => setAddPivotModalOpen(false)} />


      <mui.Grid container spacing={1} >
        <mui.Grid item xs={12} md={9}>
          <CardTypeSelect {...{ cardType, setCardType }} />
          <div
            ref={mapContainerRef}
            style={{ height: pageContentHeight, width: "100%" }}
            onContextMenu={suppressNativeContextMenu}
            onContextMenuCapture={suppressNativeContextMenu}
          >
            <MapTypeSelect {...{ mapViewType, setMapViewType }} className={mapTypeStyle} isHorizontal={useMediaQuery(theme.breakpoints.up('sm'))} />
            <GoogleMapReact
              bootstrapURLKeys={{
                key: Globals.GOOGLE_MAPS_API_KEY
              }}
              center={mapCenter || defaultCenter}
              zoom={mapZoom}
              onGoogleApiLoaded={a => {
                mapRef.current = a.map;
                mapsApiRef.current = a.maps;
                a.map.getDiv()?.addEventListener("contextmenu", suppressNativeContextMenu, true);
                a.map.addListener("rightclick", (event: any) => {
                  event?.domEvent?.preventDefault?.();
                  event?.domEvent?.stopPropagation?.();
                  const clickedLat = event?.latLng?.lat?.();
                  const clickedLng = event?.latLng?.lng?.();
                  const mouseX = event?.domEvent?.clientX;
                  const mouseY = event?.domEvent?.clientY;

                  if (
                    typeof clickedLat === "number" &&
                    typeof clickedLng === "number" &&
                    typeof mouseX === "number" &&
                    typeof mouseY === "number"
                  ) {
                    setMapContextMenu({
                      mouseX,
                      mouseY,
                      lat: clickedLat,
                      lng: clickedLng
                    });
                    return;
                  }

                  if (typeof clickedLat === "number" && typeof clickedLng === "number") {
                    copyLatLon(clickedLat, clickedLng);
                  }
                });
                setMapRefIsLoaded(true);
              }}
              onChange={e => {
                loadSensorData(e.bounds);
                loadFieldData(e.bounds);
                setMapZoom(e.zoom);
                setBounds(e.bounds);
              }}
              onChildClick={(key, childProps) => {
                if (
                  childProps.lat &&
                  childProps.lng &&
                  !(cardType === HomeCard.Pivots && childProps.category === MarkerCategory.Pivot)
                ) {
                  setMapCenter({ lat: parseFloat(childProps.lat), lng: parseFloat(childProps.lng) });
                }
              }}
              yesIWantToUseGoogleMapApiInternals={true}
              options={{ mapTypeId: mapViewType }}
            >
              {clusters.map(c => {
                const [longitude, latitude] = c.geometry.coordinates;
                if (!c.properties.cluster) {
                  return (
                    <MapMarker
                      key={`${c.properties.type}-${c.properties.id}`}
                      lat={latitude}
                      lng={longitude}
                      id={c.properties.id}
                      name={c.properties.name}
                      serialNumber={c.properties.serialNumber}
                      markerImageType={c.properties.type}
                      category={c.properties.category}
                      status={c.properties.status}
                      unitState={
                        c.properties.unitState ? c.properties.unitState : 0
                      }
                      online={
                        c.properties.online ? c.properties.online : false
                      }
                      currentEngData={c.properties.currentEngData}
                      associatedValves={c.properties.associatedValves}
                      associatedPumps={c.properties.associatedPumps}
                      isTooltipOpen={
                        c.properties.category === MarkerCategory.Pivot
                          ? selectedPivotId === c.properties.id
                          : undefined
                      }
                      onMarkerClick={
                        c.properties.category === MarkerCategory.Pivot
                          ? () => setSelectedPivotId(c.properties.id)
                          : undefined
                      }
                      onTooltipOpen={
                        c.properties.category === MarkerCategory.Pivot
                          ? () => setSelectedPivotId(c.properties.id)
                          : undefined
                      }
                      onTooltipClose={
                        c.properties.category === MarkerCategory.Pivot
                          ? () => setSelectedPivotId(currentId => currentId === c.properties.id ? null : currentId)
                          : undefined
                      }
                    />
                  );
                } else {
                  return (
                    <ClusterMarker
                      key={c.id}
                      lat={latitude}
                      lng={longitude}
                      clusterCount={c.properties.point_count}
                    />
                  );
                }
              })}
            </GoogleMapReact>
          </div>
        </mui.Grid>
        <mui.Grid item xs={12} md={3}>

          <div style={{ height: listContentHeight, overflowY: "auto" }} id="cardGrid">
            {role !== UserRole.Admin && (
              <mui.Button
                className={stopButton}
                color="secondary"
                variant="contained"
                disabled={cardType !== HomeCard.Pumps}
                fullWidth
                onClick={tryStopAllPumps}
              >
                Stop All Pumps
              </mui.Button>
            )}
            <mui.TextField
              className={searchBar}
              placeholder="Search..."
              fullWidth
              variant="outlined"
              value={searchText}
              onChange={e => setSearchText(e.currentTarget.value)}
              InputProps={{
                endAdornment: (
                  <mui.InputAdornment position="end">
                    <SearchRounded />
                  </mui.InputAdornment>
                )
              }}
            />
            {filteredPumps.length === 0 &&
              <div className={buttonContainer}>
                <Link to="/admin/pumps?add=true">
                  <Button variant="outlined" color="primary">
                    <AddCircleOutline />
                    <span>Add Pump</span>
                  </Button>
                </Link>
              </div>
            }
            {cardType === HomeCard.Sensors
              ? filteredSensors.map(s => (
                <SensorCard key={s.id} sensor={s} />
              ))
              : cardType === HomeCard.Fields
                ? filteredFields.map(f => (
                  <FieldCard key={f.id} field={f} />
                ))
                : cardType === HomeCard.Pivots
                  ? <>
                    <div className={buttonContainerRight}>
                      <Button variant="outlined" color="primary" onClick={() => setAddPivotModalOpen(true)}>
                        <AddCircleOutline />
                        <span>Add Pivot</span>
                      </Button>
                    </div>
                    {filteredPivots.map(p => (
                      <PivotCard key={p.id} pivot={p} />
                    ))}
                  </>
                : cardType === HomeCard.PumpWeather
                  ? filteredPumps.map(p => (
                    <PumpWeatherCard key={p.id} pump={p} />
                  ))
                  : filteredPumps.map(p => (
                    <PumpCard key={p.id} pump={p} />
                  ))
            }
          </div>
        </mui.Grid>
      </mui.Grid>
      <mui.Menu
        keepMounted
        open={!!mapContextMenu}
        onClose={closeMapContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          mapContextMenu
            ? { top: mapContextMenu.mouseY, left: mapContextMenu.mouseX }
            : undefined
        }
      >
        <mui.MenuItem
          onClick={() => {
            if (mapContextMenu) {
              copyLatLon(mapContextMenu.lat, mapContextMenu.lng);
            }
            closeMapContextMenu();
          }}
        >
          Copy Coordinates
        </mui.MenuItem>
        {cardType === HomeCard.Pivots && (
          <mui.MenuItem
            onClick={() => {
              setShowPivotGraphics(current => !current);
              closeMapContextMenu();
            }}
          >
            {showPivotGraphics ? "Hide Graphics" : "Show Graphics"}
          </mui.MenuItem>
        )}
      </mui.Menu>
    </div>
  );
};

const useStyles = makeStyles((theme: any) => ({
  searchBar: {
    backgroundColor: theme.whiteColor,
    position: "sticky",
    top: 0,
    zIndex: 1
  },
  stopButton: {
    color: "error",
    padding: "4px",
    position: "sticky",
    top: 0,
    display: "flex",
    justifySelf: "center",
    marginBottom: theme.standardMargin,
    textTransform: "none",
    fontWeight: "bold"
  },
  visiblityButton: {
    color: theme.whiteColor,
    padding: "16px",
    position: "sticky",
    top: 0,
    display: "flex",
    justifySelf: "center",
    marginBottom: theme.standardMargin,
    textTransform: "none",
    fontWeight: "bold"
  },
  mapTypeStyle: {
    position: "absolute",
    zIndex: 100,
    color: "white"
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.standardMargin
  },
  buttonContainerRight: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.standardMargin,
    gap: theme.spacing(1)
  }

}));
